// File Attachments System Types

export interface FileAttachment {
  id: string;
  filename: string;
  original_filename: string;
  file_size: number;
  mime_type: string;
  file_extension: string;
  storage_path: string;
  checksum: string;
  title?: string;
  description?: string;
  tags: string[];
  metadata: Record<string, any>;
  uploaded_by: string;
  uploader_name: string;
  school_id?: string;
  entity_type: EntityType;
  entity_id?: string;
  access_level: AccessLevel;
  allowed_roles: string[];
  expiry_date?: string;
  version_number: number;
  parent_file_id?: string;
  is_current_version: boolean;
  version_notes?: string;
  is_virus_scanned: boolean;
  virus_scan_result?: string;
  download_count: number;
  last_accessed?: string;
  share_token?: string;
  share_expires_at?: string;
  share_download_limit?: number;
  share_download_count: number;
  status: FileStatus;
  processing_error?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

export type EntityType = 
  | 'support_request' 
  | 'support_comment' 
  | 'student_record' 
  | 'application' 
  | 'school_document' 
  | 'general';

export type AccessLevel = 
  | 'private' 
  | 'school_only' 
  | 'admin_only' 
  | 'public';

export type FileStatus = 
  | 'uploading' 
  | 'processing' 
  | 'ready' 
  | 'error' 
  | 'archived' 
  | 'deleted';

export interface FileAccessLog {
  id: string;
  file_id: string;
  user_id?: string;
  action: FileAction;
  ip_address?: string;
  user_agent?: string;
  additional_data: Record<string, any>;
  created_at: string;
}

export type FileAction = 
  | 'view' 
  | 'download' 
  | 'delete' 
  | 'share' 
  | 'version_create';

export interface FileShare {
  id: string;
  file_id: string;
  shared_by: string;
  shared_with_email?: string;
  shared_with_user_id?: string;
  access_type: ShareAccessType;
  expires_at?: string;
  is_active: boolean;
  created_at: string;
}

export type ShareAccessType = 
  | 'view' 
  | 'download' 
  | 'edit';

// Upload related types
export interface FileUploadOptions {
  entity_type: EntityType;
  entity_id?: string;
  access_level?: AccessLevel;
  title?: string;
  description?: string;
  tags?: string[];
  expiry_date?: Date;
  allowed_roles?: string[];
}

export interface FileUploadProgress {
  file: File;
  progress: number;
  status: 'waiting' | 'uploading' | 'processing' | 'success' | 'error';
  error?: string;
  attachment_id?: string;
}

export interface FileValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface FilePreviewProps {
  attachment: FileAttachment;
  showActions?: boolean;
  onDownload?: (attachment: FileAttachment) => void;
  onDelete?: (attachment: FileAttachment) => void;
  onShare?: (attachment: FileAttachment) => void;
  onVersions?: (attachment: FileAttachment) => void;
}

export interface FileUploadZoneProps {
  onFilesSelected: (files: File[]) => void;
  acceptedTypes?: string[];
  maxFileSize?: number;
  maxFiles?: number;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export interface FileManagerProps {
  entity_type?: EntityType;
  entity_id?: string;
  school_id?: string;
  showUpload?: boolean;
  showFilters?: boolean;
  onFileSelect?: (attachment: FileAttachment) => void;
  onFilesChange?: (attachments: FileAttachment[]) => void;
}

export interface FileSecurityModalProps {
  isOpen: boolean;
  onClose: () => void;
  attachment: FileAttachment;
  onSave: (updates: Partial<FileAttachment>) => Promise<void>;
}

// Constants for file validation
export const ALLOWED_FILE_TYPES = {
  images: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  documents: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv'
  ],
  all: [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv'
  ]
};

export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
export const MAX_FILES_PER_UPLOAD = 10;

// Utility functions
export function getFileTypeCategory(mimeType: string): 'image' | 'document' | 'unknown' {
  if (ALLOWED_FILE_TYPES.images.includes(mimeType)) {
    return 'image';
  }
  if (ALLOWED_FILE_TYPES.documents.includes(mimeType)) {
    return 'document';
  }
  return 'unknown';
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function getFileIcon(mimeType: string): string {
  switch (getFileTypeCategory(mimeType)) {
    case 'image':
      return '🖼️';
    case 'document':
      if (mimeType === 'application/pdf') return '📄';
      if (mimeType.includes('word')) return '📝';
      if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
      return '📄';
    default:
      return '📎';
  }
}

export function validateFile(file: File): FileValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    errors.push(`File size (${formatFileSize(file.size)}) exceeds maximum allowed size (${formatFileSize(MAX_FILE_SIZE)})`);
  }

  // Check file type
  if (!ALLOWED_FILE_TYPES.all.includes(file.type)) {
    errors.push(`File type ${file.type} is not allowed`);
  }

  // Check file name
  if (file.name.length > 255) {
    errors.push('File name is too long (maximum 255 characters)');
  }

  // Warnings for large files
  if (file.size > 10 * 1024 * 1024) { // 10MB
    warnings.push('Large file size may take longer to upload');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}